/* Reports — analytics dashboard: pipeline funnel, top skills, submissions per client/recruiter. */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { BarChart3, TrendingUp, Users, Briefcase, Mail, Building2 } from "lucide-react";

const STATUSES = [
  { key: "draft", label: "Draft", color: "bg-slate-500" },
  { key: "ready", label: "Ready", color: "bg-sky-500" },
  { key: "submitted", label: "Submitted", color: "bg-indigo-500" },
  { key: "interview", label: "Interview", color: "bg-violet-500" },
  { key: "offer", label: "Offer", color: "bg-orange-500" },
  { key: "placed", label: "Placed", color: "bg-emerald-500" },
  { key: "hold", label: "Hold", color: "bg-amber-500" },
  { key: "rejected", label: "Rejected", color: "bg-rose-500" },
];

export default function Reports() {
  const [m, setM] = useState(null);
  const [insights, setInsights] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    api.get("/dashboard/metrics").then((r) => setM(r.data));
    api.get("/dashboard/insights").then((r) => setInsights(r.data));
    api.get("/email/logs").then((r) => setLogs(r.data || [])).catch(() => {});
  }, []);

  const topClients = useMemo(() => {
    const counts = {};
    for (const l of logs) {
      const domain = (l.to_email || "").split("@")[1] || "unknown";
      const name = l.resume?.client_name || domain;
      counts[name] = (counts[name] || 0) + 1;
    }
    return Object.entries(counts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 8);
  }, [logs]);

  const conversionPct = useMemo(() => {
    if (!m) return null;
    const pc = m.pipeline_counts || {};
    const submitted = (pc.submitted || 0) + (pc.interview || 0) + (pc.offer || 0) + (pc.placed || 0);
    const placed = pc.placed || 0;
    return {
      submit: m.total ? Math.round((submitted / m.total) * 100) : 0,
      place: submitted ? Math.round((placed / submitted) * 100) : 0,
    };
  }, [m]);

  if (!m) return <div className="p-10 text-muted-foreground">Loading…</div>;

  const total = m.total || 0;
  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold flex items-center gap-2"><BarChart3 className="size-3.5" /> Reports</div>
            <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">Workspace analytics</h1>
            <p className="text-muted-foreground mt-1 text-sm">Pipeline funnel, top skills, submissions per client and recruiter.</p>
          </div>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/export/excel`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm px-4 h-10 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="rep-download-excel">
            <BarChart3 className="size-4" /> Download Excel report
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <StatCard icon={Users} label="Total resumes" value={total} testid="rep-total" />
        <StatCard icon={TrendingUp} label="New this week" value={m.new_this_week || 0} testid="rep-weekly" />
        <StatCard icon={Briefcase} label="Job profiles" value={m.job_profiles_count || 0} testid="rep-jp" />
        <StatCard icon={Mail} label="Submissions sent" value={m.submissions_count || 0} testid="rep-submissions" />
      </div>

      {/* Pipeline funnel + conversion */}
      <Card className="border-border mb-4">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="display font-bold text-lg">Pipeline funnel</div>
            {conversionPct && (
              <div className="text-xs text-muted-foreground flex items-center gap-3">
                <span><span className="font-bold text-foreground">{conversionPct.submit}%</span> submit rate</span>
                <span><span className="font-bold text-foreground">{conversionPct.place}%</span> placement rate</span>
              </div>
            )}
          </div>
          <div className="space-y-2">
            {STATUSES.map((s) => {
              const c = (m.pipeline_counts || {})[s.key] || 0;
              const pct = total ? Math.round((c / total) * 100) : 0;
              return (
                <div key={s.key} data-testid={`rep-status-${s.key}`}>
                  <div className="flex justify-between text-xs mb-1"><span className="flex items-center gap-2"><span className={`size-1.5 rounded-full ${s.color}`} /><span className="font-semibold">{s.label}</span></span><span className="text-muted-foreground">{c} · {pct}%</span></div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden"><div className={`h-full ${s.color} opacity-80`} style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Top skills */}
        {insights?.top_skills?.length > 0 && (
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="display font-bold text-lg mb-3">Top skills in library</div>
              <div className="flex flex-wrap gap-1.5" data-testid="rep-top-skills">
                {insights.top_skills.map((s, i) => <span key={i} className="chip bg-primary/10 text-primary border-primary/30">{s.name} · {s.count}</span>)}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top recruiters */}
        {m.top_recruiters?.length > 0 && (
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="display font-bold text-lg mb-3">Top recruiters</div>
              <div className="space-y-1.5" data-testid="rep-top-recruiters">
                {m.top_recruiters.map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1">
                    <div>
                      <div className="font-semibold">{r.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.role}</div>
                    </div>
                    <div className="text-lg font-black">{r.count}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Top clients (by domain/client_name) */}
      <Card className="border-border mb-4" data-testid="rep-top-clients">
        <CardContent className="p-5">
          <div className="display font-bold text-lg mb-3 flex items-center gap-2"><Building2 className="size-4" /> Submissions per client</div>
          {topClients.length === 0 ? (
            <div className="text-sm text-muted-foreground">No submissions yet. Send a resume to a client to see data here.</div>
          ) : (
            <div className="space-y-2" data-testid="rep-top-clients-list">
              {topClients.map((c) => {
                const maxC = topClients[0].count || 1;
                const pct = Math.round((c.count / maxC) * 100);
                return (
                  <div key={c.name}>
                    <div className="flex justify-between text-xs mb-1"><span className="font-semibold">{c.name}</span><span className="text-muted-foreground">{c.count} submission{c.count > 1 ? "s" : ""}</span></div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-primary/80" style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template usage */}
      {m.template_usage?.length > 0 && (
        <Card className="border-border mb-4">
          <CardContent className="p-5">
            <div className="display font-bold text-lg mb-3">Template usage</div>
            <div className="space-y-2" data-testid="rep-template-usage">
              {m.template_usage.map((t, i) => {
                const pct = total ? Math.round((t.count / total) * 100) : 0;
                return (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-1"><span className="font-semibold">{t.name}</span><span className="text-muted-foreground">{t.count} · {pct}%</span></div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-indigo-500/70" style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <Card className="border-border" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">{label}</div>
          <Icon className="size-4 text-muted-foreground" />
        </div>
        <div className="mt-2 display text-3xl font-extrabold">{value}</div>
      </CardContent>
    </Card>
  );
}
